import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatINRFull } from "@/lib/format";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, CloudUpload, ShieldQuestion, Send, AlertTriangle, Download } from "lucide-react";
import { SourceBadge } from "@/components/v3/SourceBadge";

const SECTIONS = [
  { key: "company", label: "Company" }, { key: "promoters", label: "Promoters" },
  { key: "financials", label: "Financials" }, { key: "existing_debt", label: "Existing Debt" },
  { key: "banking", label: "Banking" }, { key: "gst", label: "GST / Tax" },
  { key: "collateral", label: "Collateral" }, { key: "documents", label: "Documents" },
  { key: "review", label: "Review & Submit" },
];
const inputCls = "w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white";
const F = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</label>
    {children}
  </div>
);

export default function Apply() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [appId, setAppId] = useState(params.get("id") || null);
  const [meta, setMeta] = useState(null); // full app doc
  const [sections, setSections] = useState({});
  const [active, setActive] = useState("company");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const dirty = useRef(false);
  const appIdRef = useRef(appId);
  appIdRef.current = appId;

  useEffect(() => {
    if (params.get("id")) {
      api.get(`/v3/applications/${params.get("id")}`)
        .then((r) => { setMeta(r.data.application); setSections(r.data.application.sections || {}); })
        .catch(() => toast.error("Could not load draft"));
    }
  }, [params]);

  const persist = useCallback(async (secs) => {
    setSaving(true);
    try {
      let id = appIdRef.current;
      if (!id) {
        const name = secs.company?.company_Name;
        if (!name) { setSaving(false); return; }
        const r = await api.post("/v3/applications", { company_Name: name, stage: "lead" });
        id = r.data.application.v3_id;
        setAppId(id);
        window.history.replaceState(null, "", `/apply?id=${id}`);
      }
      const r2 = await api.patch(`/v3/applications/${id}`, { sections: secs });
      setMeta(r2.data.application);
      setSavedAt(new Date());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }, []);

  useEffect(() => {
    if (!dirty.current) return;
    const t = setTimeout(() => { dirty.current = false; persist(sections); }, 900);
    return () => clearTimeout(t);
  }, [sections, persist]);

  const upd = (sec, patch) => {
    setSections((s) => ({ ...s, [sec]: { ...(s[sec] || {}), ...patch } }));
    dirty.current = true;
  };
  const updList = (sec, list) => { setSections((s) => ({ ...s, [sec]: list })); dirty.current = true; };
  const setFlag = (k, v) => { setSections((s) => ({ ...s, [k]: v })); dirty.current = true; };

  const mockVerify = (what) => {
    if (!appId) { toast.error("Save the draft first (enter company name)"); return; }
    api.post(`/v3/applications/${appId}/mock-verify`, { what })
      .then((r) => { setMeta((m) => ({ ...m, verifications: { ...(m?.verifications || {}), [what]: r.data.result } })); toast.success(`${what.toUpperCase()} MOCK-verified (simulated — no real check)`); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  const uploadDoc = (file) => {
    if (!appId) { toast.error("Save the draft first (enter company name)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    api.post(`/v3/applications/${appId}/documents`, fd)
      .then((r) => {
        setMeta((m) => ({ ...m, documents: [...(m?.documents || []), r.data.document], completeness: r.data.completeness }));
        toast.success(`Uploaded ${file.name} to object storage`);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  const submit = () => {
    api.post(`/v3/applications/${appId}/submit`)
      .then(() => { toast.success("Application submitted"); navigate("/dsa-console"); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  const c = sections.company || {};
  const promoters = sections.promoters || [];
  const debts = sections.existing_debt || [];
  const vf = meta?.verifications || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl" data-testid="apply-page">
      <header className="mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="ceo-label !text-[#b3122a]">Originate · Build 3</span>
          <SourceBadge source="TEST_V3" />
          {meta?.applicant_no && <span className="text-[11px] font-mono text-slate-400">{meta.applicant_no}</span>}
        </div>
        <h1 className="ceo-fig text-2xl sm:text-3xl text-[#111111] mt-1">New Test Application</h1>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-36 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#b3122a] transition-all" style={{ width: `${meta?.completeness || 0}%` }} />
            </div>
            <span className="text-[12px] font-bold tabular-nums text-slate-700" data-testid="apply-completeness">{meta?.completeness || 0}%</span>
          </div>
          <span className="text-[11px] text-slate-400" data-testid="autosave-indicator">
            {saving ? "Saving…" : savedAt ? `Autosaved ${savedAt.toLocaleTimeString()}` : appId ? "Draft loaded" : "Enter company name to start autosave"}
          </span>
        </div>
        {meta?.stage === "info_required" && meta?.info_request && (
          <div className="mt-3 flex items-start gap-2 border border-amber-300 bg-amber-50 rounded px-3 py-2.5" data-testid="info-request-banner">
            <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="text-[12px] text-amber-800"><b>Additional information requested:</b> {meta.info_request.message}
              <span className="text-amber-600"> — {meta.info_request.requested_by}</span></div>
          </div>
        )}
      </header>

      <div className="flex flex-wrap gap-1.5 pb-2 mb-5" data-testid="apply-stepper">
        {SECTIONS.map((s, i) => (
          <button key={s.key} onClick={() => setActive(s.key)} data-testid={`step-${s.key}`}
            className={`shrink-0 text-[11px] font-bold uppercase tracking-wide rounded px-2.5 py-1.5 border transition-colors ${active === s.key ? "border-[#b3122a] text-[#b3122a] bg-[#b3122a]/5" : "border-slate-200 bg-white text-slate-500"}`}>
            {i + 1}. {s.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6" data-testid={`section-${active}`}>
        {active === "company" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Company / entity name"><input data-testid="f-company-name" className={inputCls} value={c.company_Name || ""} onChange={(e) => upd("company", { company_Name: e.target.value })} /></F>
            <F label="Entity type">
              <select data-testid="f-entity-type" className={inputCls} value={c.entity_type || ""} onChange={(e) => upd("company", { entity_type: e.target.value })}>
                <option value="">Select…</option>
                {["Private Limited", "LLP", "Partnership", "Proprietorship", "Public Limited"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </F>
            <F label="PAN">
              <div className="flex gap-2">
                <input data-testid="f-pan" className={inputCls} value={c.pan || ""} onChange={(e) => upd("company", { pan: e.target.value.toUpperCase() })} />
                <button data-testid="mock-verify-pan" onClick={() => mockVerify("pan")}
                  className="shrink-0 inline-flex items-center gap-1 border border-violet-300 text-violet-700 text-[11px] font-bold rounded px-2">
                  <ShieldQuestion size={12} /> {vf.pan ? "MOCK ✓" : "Verify (MOCK)"}
                </button>
              </div>
            </F>
            <F label="GSTIN">
              <div className="flex gap-2">
                <input data-testid="f-gstin" className={inputCls} value={c.gstin || ""} onChange={(e) => upd("company", { gstin: e.target.value.toUpperCase() })} />
                <button data-testid="mock-verify-gstin" onClick={() => mockVerify("gstin")}
                  className="shrink-0 inline-flex items-center gap-1 border border-violet-300 text-violet-700 text-[11px] font-bold rounded px-2">
                  <ShieldQuestion size={12} /> {vf.gstin ? "MOCK ✓" : "Verify (MOCK)"}
                </button>
              </div>
            </F>
            <F label="CIN / LLPIN (if applicable)"><input data-testid="f-cin" className={inputCls} value={c.cin || ""} onChange={(e) => upd("company", { cin: e.target.value })} /></F>
            <F label="Location (city, state)"><input data-testid="f-city" className={inputCls} value={c.city || ""} onChange={(e) => upd("company", { city: e.target.value })} /></F>
            <F label="Years in business"><input data-testid="f-years" type="number" className={inputCls} value={c.years_in_business || ""} onChange={(e) => upd("company", { years_in_business: e.target.value })} /></F>
            <F label="Annual turnover (₹)"><input data-testid="f-turnover" type="number" className={inputCls} value={c.turnover || ""} onChange={(e) => upd("company", { turnover: e.target.value })} /></F>
            <F label="Amount requested (₹)"><input data-testid="f-amount" type="number" className={inputCls} value={c.amount || ""} onChange={(e) => upd("company", { amount: e.target.value })} /></F>
            <F label="Tenor (months)"><input data-testid="f-tenor" type="number" className={inputCls} value={c.tenor || ""} onChange={(e) => upd("company", { tenor: e.target.value })} /></F>
            <div className="sm:col-span-2"><F label="Purpose of loan"><textarea data-testid="f-purpose" rows={2} className={inputCls} value={c.purpose || ""} onChange={(e) => upd("company", { purpose: e.target.value })} /></F></div>
          </div>
        )}

        {active === "promoters" && (
          <div className="space-y-3">
            {promoters.map((p, i) => (
              <div key={i} className="grid sm:grid-cols-4 gap-2 border border-slate-100 rounded p-3">
                <input placeholder="Name" className={inputCls} value={p.name || ""} onChange={(e) => updList("promoters", promoters.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} data-testid={`promoter-name-${i}`} />
                <input placeholder="PAN" className={inputCls} value={p.pan || ""} onChange={(e) => updList("promoters", promoters.map((x, j) => j === i ? { ...x, pan: e.target.value.toUpperCase() } : x))} />
                <input placeholder="Net worth (₹)" type="number" className={inputCls} value={p.net_worth || ""} onChange={(e) => updList("promoters", promoters.map((x, j) => j === i ? { ...x, net_worth: e.target.value } : x))} />
                <button onClick={() => updList("promoters", promoters.filter((_, j) => j !== i))} className="text-[12px] text-[#b3122a] font-semibold">Remove</button>
              </div>
            ))}
            <button data-testid="add-promoter-btn" onClick={() => updList("promoters", [...promoters, {}])}
              className="border border-slate-300 rounded px-3 py-1.5 text-sm font-semibold text-slate-700">+ Add promoter</button>
          </div>
        )}

        {active === "financials" && (
          <div className="grid sm:grid-cols-3 gap-4">
            <F label="Latest FY turnover (₹)"><input data-testid="f-fin-turnover" type="number" className={inputCls} value={(sections.financials || {}).turnover_latest || ""} onChange={(e) => upd("financials", { turnover_latest: e.target.value })} /></F>
            <F label="EBITDA (₹)"><input data-testid="f-fin-ebitda" type="number" className={inputCls} value={(sections.financials || {}).ebitda || ""} onChange={(e) => upd("financials", { ebitda: e.target.value })} /></F>
            <F label="PAT (₹)"><input data-testid="f-fin-pat" type="number" className={inputCls} value={(sections.financials || {}).pat || ""} onChange={(e) => upd("financials", { pat: e.target.value })} /></F>
          </div>
        )}

        {active === "existing_debt" && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="no-debt-checkbox" checked={sections.no_existing_debt === true}
                onChange={(e) => setFlag("no_existing_debt", e.target.checked)} />
              No existing borrowings — declared
            </label>
            {debts.map((d, i) => (
              <div key={i} className="grid sm:grid-cols-4 gap-2 border border-slate-100 rounded p-3">
                <input placeholder="Lender" className={inputCls} value={d.lender || ""} onChange={(e) => updList("existing_debt", debts.map((x, j) => j === i ? { ...x, lender: e.target.value } : x))} data-testid={`debt-lender-${i}`} />
                <input placeholder="Outstanding (₹)" type="number" className={inputCls} value={d.outstanding || ""} onChange={(e) => updList("existing_debt", debts.map((x, j) => j === i ? { ...x, outstanding: e.target.value } : x))} />
                <input placeholder="EMI (₹/mo)" type="number" className={inputCls} value={d.emi || ""} onChange={(e) => updList("existing_debt", debts.map((x, j) => j === i ? { ...x, emi: e.target.value } : x))} />
                <button onClick={() => updList("existing_debt", debts.filter((_, j) => j !== i))} className="text-[12px] text-[#b3122a] font-semibold">Remove</button>
              </div>
            ))}
            <button data-testid="add-debt-btn" onClick={() => updList("existing_debt", [...debts, {}])}
              className="border border-slate-300 rounded px-3 py-1.5 text-sm font-semibold text-slate-700">+ Add borrowing</button>
          </div>
        )}

        {active === "banking" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Primary bank"><input data-testid="f-bank-name" className={inputCls} value={(sections.banking || {}).bank_name || ""} onChange={(e) => upd("banking", { bank_name: e.target.value })} /></F>
            <F label="Avg monthly credits (₹)"><input data-testid="f-bank-credits" type="number" className={inputCls} value={(sections.banking || {}).avg_monthly_credits || ""} onChange={(e) => upd("banking", { avg_monthly_credits: e.target.value })} /></F>
            <div className="sm:col-span-2 text-[11px] text-slate-400">Bank-statement analysis via Account Aggregator will be a <SourceBadge source="MOCK" /> adapter until connected.</div>
          </div>
        )}

        {active === "gst" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="GSTIN"><input data-testid="f-gst-gstin" className={inputCls} value={(sections.gst || {}).gstin || c.gstin || ""} onChange={(e) => upd("gst", { gstin: e.target.value.toUpperCase() })} /></F>
            <F label="Filing status">
              <select data-testid="f-gst-filing" className={inputCls} value={(sections.gst || {}).filing_status || ""} onChange={(e) => upd("gst", { filing_status: e.target.value })}>
                <option value="">Select…</option>
                {["Regular filer", "Partial compliance", "Not filing"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </F>
          </div>
        )}

        {active === "collateral" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Collateral type">
              <select data-testid="f-col-type" className={inputCls} value={(sections.collateral || {}).type || ""} onChange={(e) => upd("collateral", { type: e.target.value })}>
                <option value="">Select…</option>
                {["Self-occupied residential", "Commercial property", "Industrial property", "Other residential", "Agricultural land", "Third-party property"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </F>
            <F label="Owner">
              <select data-testid="f-col-owner" className={inputCls} value={(sections.collateral || {}).owner || ""} onChange={(e) => upd("collateral", { owner: e.target.value })}>
                <option value="">Select…</option>
                {["Borrower company", "Promoter", "Family member", "Third party"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </F>
            <F label="Location"><input data-testid="f-col-location" className={inputCls} value={(sections.collateral || {}).location || ""} onChange={(e) => upd("collateral", { location: e.target.value })} /></F>
            <F label="Estimated value (₹)"><input data-testid="f-col-value" type="number" className={inputCls} value={(sections.collateral || {}).est_value || ""} onChange={(e) => upd("collateral", { est_value: e.target.value })} /></F>
            {["Third party", "Family member"].includes((sections.collateral || {}).owner) && (
              <div className="sm:col-span-2 border border-[#b3122a]/40 bg-red-50 rounded px-3 py-2 text-[12px] font-bold text-[#b3122a]" data-testid="third-party-flag">
                THIRD-PARTY COLLATERAL — enhanced diligence will be required at underwriting.
              </div>
            )}
          </div>
        )}

        {active === "documents" && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-sm p-6 justify-center cursor-pointer hover:border-[#b3122a]/50">
              <CloudUpload size={18} className="text-slate-400" />
              <span className="text-sm text-slate-600">Upload a NEW test document (max 8 MB)</span>
              <input data-testid="doc-upload-input" type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(e.target.files[0])} />
            </label>
            {(meta?.documents || []).filter((d) => !d.is_deleted).map((d) => (
              <div key={d.doc_id} className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2" data-testid="doc-row">
                <span className="text-sm text-slate-800 flex-1 truncate">{d.name}</span>
                <span className="text-[11px] text-slate-400 tabular-nums">{Math.round(d.size / 1024)} KB</span>
                <a className="text-[#b3122a]" data-testid="doc-download-link"
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/v3/applications/${appId}/documents/${d.doc_id}`}
                  target="_blank" rel="noreferrer"><Download size={14} /></a>
              </div>
            ))}
            <div className="text-[11px] text-slate-400">Historical loan documents are never copied — they stay referenced in the legacy DMS.</div>
          </div>
        )}

        {active === "review" && (
          <div className="space-y-4" data-testid="review-section">
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              {[["Company", c.company_Name], ["Entity", c.entity_type], ["PAN", c.pan], ["Amount", c.amount && formatINRFull(c.amount)],
                ["Tenor", c.tenor && `${c.tenor} mo`], ["Collateral", (sections.collateral || {}).type],
                ["Collateral value", (sections.collateral || {}).est_value && formatINRFull(sections.collateral.est_value)],
                ["Documents", (meta?.documents || []).length]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 py-1.5">
                  <span className="text-slate-400">{k}</span><span className="font-semibold text-slate-800">{v || "—"}</span>
                </div>
              ))}
            </div>
            {(meta?.missing_items || []).length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded p-3" data-testid="missing-items">
                <div className="text-[12px] font-bold text-amber-800 mb-1">Missing items ({meta.missing_items.length})</div>
                <ul className="text-[12px] text-amber-700 list-disc pl-4 space-y-0.5">
                  {meta.missing_items.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
            <button data-testid="submit-application-btn" onClick={submit} disabled={!appId}
              className="inline-flex items-center gap-2 bg-[#b3122a] text-white text-sm font-semibold rounded px-5 py-2.5 hover:bg-[#8f0e21] transition-colors disabled:opacity-50">
              <Send size={15} /> Submit application
            </button>
            <div className="text-[11px] text-slate-400">You can submit with missing items — Credit may send it back for additional information.</div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-4">
        <button onClick={() => setActive(SECTIONS[Math.max(0, SECTIONS.findIndex((s) => s.key === active) - 1)].key)}
          className="text-sm font-semibold text-slate-500" data-testid="step-prev">← Previous</button>
        <button onClick={() => setActive(SECTIONS[Math.min(SECTIONS.length - 1, SECTIONS.findIndex((s) => s.key === active) + 1)].key)}
          className="text-sm font-semibold text-[#b3122a] inline-flex items-center gap-1" data-testid="step-next">Next <Check size={14} /></button>
      </div>
    </div>
  );
}
